import { Button } from '@/app/common/components/button/Button'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { cn } from '@/app/common/utilities'
import { Alert, AlertDescription } from '@/shad/ui/alert'
import { Badge } from '@/shad/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shad/ui/dialog'
import { AlertTriangle, Power, PowerOff } from 'lucide-react'
import { useState } from 'react'
import { useBulkUpdateRuleStatus } from '../hooks/useBulkUpdateRuleStatus'
import { ApprovalRule } from '../schema'
import { removeUnderscoresAndMakeFirstLetterCapital } from '../utils'
import { useSelector } from 'react-redux'
import { selectSelectedProgram } from '@/storage/programs/slice'

interface BulkActionsProps {
  selectedRuleIds: number[]
  rules: ApprovalRule[]
}

export function BulkActions({ selectedRuleIds, rules }: BulkActionsProps) {
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false)
  const [pendingAction, setPendingAction] = useState<
    'enable' | 'disable' | null
  >(null)
  const programId = useSelector(selectSelectedProgram).id

  const { mutate: bulkUpdateRuleStatus, status } = useBulkUpdateRuleStatus({
    onSuccess: () => {
      setShowConfirmationDialog(false)
    },
    programId,
  })

  const handleActionClick = (action: 'enable' | 'disable') => {
    setPendingAction(action)
    setShowConfirmationDialog(true)
  }

  const handleConfirm = () => {
    if (pendingAction && selectedRuleIds.length > 0) {
      bulkUpdateRuleStatus({
        ruleIds: selectedRuleIds,
        isActive: pendingAction === 'enable' ? true : false,
      })
    }
  }

  const handleCancel = () => {
    setShowConfirmationDialog(false)
    setPendingAction(null)
  }

  // Get selected rules and check if any are already in the target state
  const selectedRules = rules.filter((rule) =>
    selectedRuleIds.includes(rule.id)
  )
  const conflictingRules = selectedRules.filter((rule) => {
    if (pendingAction === 'enable') {
      return rule.is_active // Already enabled
    } else if (pendingAction === 'disable') {
      return !rule.is_active // Already disabled
    }
    return false
  })

  const hasConflicts = conflictingRules.length > 0

  // Generate a human-readable conflict message
  const getConflictMessage = (
    action: 'enable' | 'disable',
    conflictCount: number
  ) => {
    const actionText = action === 'enable' ? 'enabled' : 'disabled'
    const ruleText = conflictCount === 1 ? 'rule is' : 'rules are'

    return `${conflictCount} ${ruleText} already ${actionText}. This action will not have any effect on them.`
  }

  if (selectedRuleIds.length === 0) {
    return null
  }

  const isLoading = status == 'pending'

  return (
    <>
      {/* Enhanced Bulk Actions Bar */}
      <div className="flex items-center justify-between mt-6 mb-6 p-4 bg-gradient-to-r  rounded-xl border ">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8  rounded-full">
            <span className="text-sm font-semibold bg-gray-100 border flex-shrink-0 border-gray-200 h-8 w-8 inline-flex items-center justify-center rounded-full">
              {selectedRuleIds.length}
            </span>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">
              {selectedRuleIds.length} rule
              {selectedRuleIds.length !== 1 ? 's' : ''} selected
            </p>
            <p className="text-xs text-gray-500">Choose an action to perform</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleActionClick('enable')}
            disabled={isLoading}
          >
            <Power className="h-4 w-4 mr-2 text-green-600" />
            Enable
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleActionClick('disable')}
            disabled={isLoading}
          >
            <PowerOff className="h-4 w-4 mr-2 text-red-600" />
            Disable
          </Button>
        </div>
      </div>

      {/* Enhanced Confirmation Dialog */}
      <Dialog open={showConfirmationDialog} onOpenChange={handleCancel}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh]">
          <DialogHeader className="pb-4">
            <DialogTitle className="text-xl font-semibold flex items-center gap-2">
              Confirm {pendingAction === 'enable' ? 'Enable' : 'Disable'} Rules
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              You're about to {pendingAction}{' '}
              <strong>{selectedRuleIds.length}</strong> rule
              {selectedRuleIds.length !== 1 ? 's' : ''}. Please review the
              selection below.
            </DialogDescription>
          </DialogHeader>

          {/* Enhanced Warning Alert */}
          {hasConflicts && (
            <Alert variant={'default'}>
              <AlertTriangle className="size-5 stroke-yellow-600" />
              <AlertDescription className="text-gray-700">
                <span className="font-medium text-yellow-800">Attention:</span>{' '}
                {pendingAction &&
                  getConflictMessage(pendingAction, conflictingRules.length)}
              </AlertDescription>
            </Alert>
          )}

          {/* Enhanced Rules List */}
          <div className="max-h-80 overflow-y-auto">
            <div className="space-y-3">
              {selectedRules.map((rule) => {
                const isConflicting = conflictingRules.some(
                  (cr) => cr.id === rule.id
                )
                return (
                  <div
                    key={rule.id}
                    className={cn('p-4 rounded-lg border transition-all')}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-gray-900">
                            {rule.name}
                          </h4>
                          {isConflicting && (
                            <Badge
                              className="inline-flex items-center"
                              variant={'yellow'}
                            >
                              <AlertTriangle className="h-3 w-3 mr-2" />
                              Already{' '}
                              {pendingAction === 'enable'
                                ? 'enabled'
                                : 'disabled'}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 bg-gray-900 rounded-full"></span>
                            Target: {rule.target_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 bg-gray-900 rounded-full"></span>
                            Type:{' '}
                            {removeUnderscoresAndMakeFirstLetterCapital(
                              rule.target_type_code
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Enhanced Dialog Footer */}
          <DialogFooter className="pt-6 border-t border-gray-100">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isLoading}
              className="px-6"
            >
              Cancel
            </Button>
            <div>
              <SubmitButton
                onClick={handleConfirm}
                status={status}
                buttonTxt={`Confirm ${pendingAction === 'enable' ? 'Enable' : 'Disable'}`}
              />
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
